import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type JSX, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { company } from '~/company/Company';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import { Spinner } from '~/components/isolated/SpinnerWithLoadMore';
import ProductCard from '~/components/ProductCard/ProductCard';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { searchStore } from '~/stores/SearchStore';
import { sortProductsByStock } from '~/utils/sortProductsByStock';

import { SearchResultsEmpty } from '../SearchResultsEmpty/SearchResultsEmpty';

import styles from './SearchResultsDesktop.module.scss';

const SearchResultsDesktop = observer((): JSX.Element => {
  const { t } = useTranslation();
  const { state } = useLocation();

  useEffect(() => {
    analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_VISITED, {
      source: state?.source || 'direct',
    });
    analyticsService.track(AnalyticsEvent.CATALOG_SEARCH_VISITED_BI, {
      source: state?.source || 'direct',
    });
  }, []);

  const sortedSearchResults = useMemo(() => {
    if (!searchStore.result) {
      return null;
    }
    return sortProductsByStock(searchStore.result);
  }, [searchStore.result]);

  const breadcrumbs = [
    { text: t('translation:home'), link: '/' },
    { text: t('translation:searchResult'), link: '/search' },
  ];

  return (
    <div className={classes(['page-catalog', styles.root])}>
      <div className={styles.container}>
        <Breadcrumbs items={breadcrumbs} className={styles.breadcrumbs} />

        <h1 className={styles.title}>{t('translation:searchResult')}</h1>

        {!(sortedSearchResults?.length ?? 0) &&
        searchStore.paginationRequest.isLoading ? (
          <Spinner />
        ) : (sortedSearchResults?.length ?? 0) !== 0 ? (
          <>
            <div className={styles.blockProducts}>
              {(sortedSearchResults ?? []).map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  variant={company.productCardVariantL}
                />
              ))}
            </div>
          </>
        ) : sortedSearchResults !== null &&
          !sortedSearchResults.length &&
          searchStore.queryString.length > 0 ? (
          <SearchResultsEmpty />
        ) : null}
      </div>

      {(sortedSearchResults?.length ?? 0) > 0 &&
        searchStore.paginationRequest.canLoadMore && (
          <Spinner
            onLoadMore={() =>
              searchStore.paginationRequest.loadMore(searchStore.queryString)
            }
          />
        )}
    </div>
  );
});

export default SearchResultsDesktop;
